import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import PieChart, { PieChartData } from '../common/PieChart';

interface NetworkSummaryCardProps {
  connections: number;
  pendingSent: number;
  pendingReceived: number;
  connectionTypeBreakdown: {
    mentor: number;
    client: number;
    supplier: number;
    others: number;
  };
}

const NetworkSummaryCard: React.FC<NetworkSummaryCardProps> = ({
  connections,
  pendingSent,
  pendingReceived,
  connectionTypeBreakdown,
}) => {
  const { theme, isDark } = useTheme();
  const navigation = useNavigation<any>();

  // Prepare pie chart data with colors
  const pieData: PieChartData[] = [
    { value: connectionTypeBreakdown.mentor, color: '#3B82F6', label: 'Mentor' },
    { value: connectionTypeBreakdown.client, color: '#10B981', label: 'Client' },
    { value: connectionTypeBreakdown.supplier, color: '#F59E0B', label: 'Supplier' },
    { value: connectionTypeBreakdown.others, color: '#8B5CF6', label: 'Others' },
  ].filter(item => item.value > 0);

  const handlePress = () => {
    navigation.navigate('Network');
  };

  return (
    <TouchableOpacity 
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: theme.primary + '20' }]}>
          <Ionicons name="people-outline" size={24} color={theme.primary} />
        </View>
        <ThemedText style={[styles.title, { color: theme.text }]}>
          Network
        </ThemedText>
      </View>

      <View style={styles.content}>
        {/* Left side - Stats */}
        <View style={styles.statsSection}>
          <View style={styles.statColumn}>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Connections
            </ThemedText>
            <ThemedText style={[styles.statValue, { color: theme.text }]}>
              {connections}
            </ThemedText>
          </View>

          <View style={styles.statColumn}>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Pending Sent
            </ThemedText>
            <ThemedText style={[styles.statValue, { color: theme.text }]}>
              {pendingSent}
            </ThemedText>
          </View>

          <View style={styles.statColumn}>
            <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
              Pending Received
            </ThemedText>
            <ThemedText style={[styles.statValue, { color: theme.text }]}>
              {pendingReceived}
            </ThemedText>
          </View>
        </View>

        {/* Right side - Pie Chart */}
        <View style={styles.chartSection}>
          <PieChart data={pieData} size={110} strokeWidth={18} showLabels={true} />
        </View>
      </View>

      {/* Legend */}
      {connections > 0 && (
        <View style={styles.legend}>
          {pieData.map((item, index) => (
            <View key={index} style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: item.color }]} />
              <ThemedText style={[styles.legendLabel, { color: theme.textSecondary }]}>
                {item.label}
              </ThemedText>
              <ThemedText style={[styles.legendValue, { color: theme.text }]}>
                {item.value}
              </ThemedText>
            </View>
          ))}
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  statsSection: {
    minWidth: 100,
  },
  statColumn: {
    flexDirection: 'column',
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 12,
    marginBottom: 2,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  chartSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  legend: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
    marginBottom: 4,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  legendLabel: {
    fontSize: 11,
    marginRight: 4,
  },
  legendValue: {
    fontSize: 11,
    fontWeight: '600',
  },
});

export default NetworkSummaryCard;
